import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Plus, TrendingUp, AlertTriangle, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';

const SSEMDashboard = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/ssem/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      1: 'text-red-600 bg-red-50',
      2: 'text-orange-600 bg-orange-50',
      3: 'text-yellow-600 bg-yellow-50',
      4: 'text-blue-600 bg-blue-50',
      5: 'text-indigo-600 bg-indigo-50',
      6: 'text-green-600 bg-green-50'
    };
    return colors[level] || 'text-gray-600 bg-gray-50';
  };

  const getLevelBadge = (level) => {
    const badges = {
      1: '⚠️ Base',
      2: '📋 Defined',
      3: '✅ Practiced',
      4: '📊 Measured',
      5: '🌟 Pervasive',
      6: '🏆 Safety as a Value'
    };
    return badges[level] || 'Not Assessed';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!dashboardData || dashboardData.current_overall_level === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to SSEM</h2>
            <p className="text-gray-600 mb-6">Saarv Safety Excellence Model - Maturity Assessment</p>
            <p className="text-gray-500 mb-8">No assessments found. Start your first quarterly assessment to measure your organization's safety maturity.</p>
            <Button
              onClick={() => navigate('/ssem/wizard')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Start New Assessment
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">SSEM Dashboard</h1>
            <p className="text-gray-600 mt-1">Saarv Safety Excellence Model - Maturity Assessment</p>
          </div>
          <div className="flex gap-3">
            {dashboardData?.latest_assessment_id && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(
                      `${process.env.REACT_APP_BACKEND_URL}/api/ssem/assessments/${dashboardData.latest_assessment_id}/export-pdf`,
                      {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      }
                    );

                    // Create blob URL and trigger download
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `SSEM_Report_${dashboardData.latest_period}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Error downloading PDF:', error);
                    showError('Failed to download PDF report. Please try again.');
                  }
                }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF Report
              </Button>
            )}
            <Button
              onClick={() => navigate('/ssem/wizard')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Assessment
            </Button>
          </div>
        </div>
      </div>

      {/* Overall Maturity Level */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Overall Maturity Level</h3>
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <div className={`text-4xl font-bold mb-2 ${getLevelColor(dashboardData.current_overall_level)}`}>
            Level {dashboardData.current_overall_level}
          </div>
          <p className="text-lg text-gray-700">{getLevelBadge(dashboardData.current_overall_level)}</p>
          <p className="text-sm text-gray-500 mt-2">Period: {dashboardData.latest_period}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Bottleneck Dimensions</h3>
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-4xl font-bold text-orange-600 mb-2">
            {dashboardData.bottleneck_dimensions?.length || 0}
          </div>
          <p className="text-sm text-gray-700">Dimensions need improvement</p>
          <div className="mt-3 space-y-1">
            {dashboardData.bottleneck_dimensions?.slice(0, 3).map((dim, idx) => (
              <div key={idx} className="text-xs text-gray-600">• {dim}</div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-600">Improvement Actions</h3>
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-4xl font-bold text-green-600 mb-2">
            {dashboardData.improvement_priority?.length || 0}
          </div>
          <p className="text-sm text-gray-700">AI-generated recommendations</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/ssem/roadmap/${dashboardData.latest_assessment_id}`)}
            className="mt-3 w-full"
          >
            View Roadmap
          </Button>
        </div>
      </div>

      {/* Dimension Scores */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Dimension Scores</h2>
          <p className="text-sm text-gray-600 mt-1">Current maturity level by dimension</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(dashboardData.dimension_scores || {}).map(([dimension, score]) => (
              <div key={dimension} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{dimension}</h3>
                  <span className={`text-2xl font-bold ${getLevelColor(score)}`}>
                    {score}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      score === 1 ? 'bg-red-500' :
                      score === 2 ? 'bg-orange-500' :
                      score === 3 ? 'bg-yellow-500' :
                      score === 4 ? 'bg-blue-500' :
                      score === 5 ? 'bg-indigo-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${(score / 6) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">{getLevelBadge(score)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trend Data */}
      {dashboardData.trend_data && dashboardData.trend_data.length > 1 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">Maturity Trend</h2>
            <p className="text-sm text-gray-600 mt-1">Historical performance</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {dashboardData.trend_data.map((trend, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{trend.period_tag}</p>
                    <p className="text-sm text-gray-600">{new Date(trend.assessment_date).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`text-2xl font-bold ${getLevelColor(trend.overall_level)}`}>
                      Level {trend.overall_level}
                    </div>
                    <div className="text-sm text-gray-600">
                      {getLevelBadge(trend.overall_level)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SSEMDashboard;